import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { getCurrentAccount } from 'api/generated/users/users';
import { failAccountSetup } from 'api/interceptors';
import { changeUserPassword } from 'api/generated/users/users';
import { logoutUser } from 'api/generated/users/users';
import { countUserInsights } from 'api/generated/users-insight/users-insight';

import { findClient } from 'hooks/api/use-clients';
import { fetchUnreadUserAssetsCount } from 'hooks/client-asset-api';

import oktaAuth, { useOkta } from 'utils/okta/use-okta';
import {
  clearOktaSessionInfo,
  ensureMarketviewRole,
  getOktaUserId,
} from 'helpers/okta-session';
import { transformRecordFromServer } from 'helpers/server-data-transformers';

import toast from 'src/components/partials/toast/toast';

export interface CurrentUserData {
  id: string | number;
  oktaUserId: string;
  authority?: string;
  admin?: boolean;
  client?: {
    id: number;
    consultants?: unknown[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface CurrentUserState {
  id: string | number | null;
  data: CurrentUserData | null;
  loading: boolean;
  error: unknown;
  filesCount: number;
  insightsCount: number;
  changingPassword?: boolean;
  passwordChangeError?: unknown;
}

interface ChangePasswordArgs {
  newPassword: string;
  oldPassword: string;
}

interface ChangePasswordError {
  status?: number;
  data?: {
    detail?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface UseCurrentUserResult extends CurrentUserState {
  getInfo: () => void;
  signOut: () => void;
  changePassword: (args: ChangePasswordArgs) => Promise<unknown>;
  setNewFilesCount: () => void;
  setNewInsightsCount: () => void;
  decrementInsightsCount: () => void;
}

export const currentUserKeys = {
  all: ['currentUser'] as const,
  detail: () => [...currentUserKeys.all, 'detail'] as const,
  filesCount: () => [...currentUserKeys.all, 'filesCount'] as const,
  insightsCount: () => [...currentUserKeys.all, 'insightsCount'] as const,
};

async function fetchCurrentUser(): Promise<CurrentUserData> {
  const oktaUserId = getOktaUserId();

  if (!oktaUserId) {
    throw new Error('No Okta session for current user request');
  }

  const userData = await getCurrentAccount(oktaUserId);

  if (!(await ensureMarketviewRole(oktaAuth))) {
    await failAccountSetup(oktaAuth);

    throw new Error('MARKETview role missing after account setup');
  }

  const stored = transformRecordFromServer('user', {
    ...userData,
    id: userData.id ?? '',
    oktaUserId: userData.oktaUserId ?? '',
  }) as CurrentUserData;

  if (stored.client?.id !== undefined && stored.client?.id !== null) {
    try {
      const client = await findClient(stored.client.id);

      return {
        ...stored,
        client: client.data as CurrentUserData['client'],
      };
    } catch {
      return stored;
    }
  }

  return stored;
}

export const useCurrentUser = (): UseCurrentUserResult => {
  const queryClient = useQueryClient();
  const okta = useOkta();

  const userQuery: UseQueryResult<CurrentUserData, Error> = useQuery({
    queryKey: currentUserKeys.detail(),
    queryFn: fetchCurrentUser,
    staleTime: Infinity,
    retry: false,
  });

  const filesCountQuery: UseQueryResult<number, Error> = useQuery({
    queryKey: currentUserKeys.filesCount(),
    queryFn: async () => {
      const data = userQuery.data;

      if (!data) return 0;

      return fetchUnreadUserAssetsCount(data.oktaUserId);
    },
    enabled: !!userQuery.data,
    staleTime: 30_000,
  });

  const insightsCountQuery: UseQueryResult<number, Error> = useQuery({
    queryKey: currentUserKeys.insightsCount(),
    queryFn: async () => {
      const data = userQuery.data;

      if (!data) return 0;

      return countUserInsights(data.oktaUserId, Number(data.id), {
        'viewed.equals': false,
      });
    },
    enabled: !!userQuery.data,
    staleTime: 30_000,
  });

  const changePasswordMutation = useMutation<
    unknown,
    ChangePasswordError,
    ChangePasswordArgs
  >({
    mutationFn: async ({ newPassword, oldPassword }) => {
      const oktaUserId = getOktaUserId();

      if (!oktaUserId) {
        const err = Object.assign(new Error('Not authenticated'), {
          status: undefined,
          data: { detail: 'Not authenticated' },
        }) as Error & ChangePasswordError;

        throw err;
      }

      return changeUserPassword(oktaUserId, { newPassword, oldPassword });
    },
    onSuccess: () => {
      toast.success({
        title: 'Password changed',
        message: `You've successfully updated your password`,
      });
    },
    onError: (error: ChangePasswordError) => {
      let addtlDetails = '';

      if (error.data && error.data.detail) {
        addtlDetails = ': ' + error.data.detail;
      }

      if (error && error.status !== 401) {
        toast.error({
          title: 'Password change failed',
          message: `An error occurred when changing your password${addtlDetails}`,
        });
      }
    },
  });

  return {
    id: userQuery.data?.id ?? null,
    data: userQuery.data ?? null,
    loading: userQuery.isLoading,
    error: userQuery.error,
    filesCount: filesCountQuery.data ?? 0,
    insightsCount: insightsCountQuery.data ?? 0,
    changingPassword: changePasswordMutation.isPending,
    passwordChangeError: changePasswordMutation.error,

    getInfo: () => {
      queryClient.invalidateQueries({ queryKey: currentUserKeys.detail() });
    },

    signOut: () => {
      const oktaUserId = getOktaUserId();

      (oktaUserId ? logoutUser(oktaUserId) : Promise.resolve())
        .catch(() => {})
        .finally(() =>
          okta
            .signOut()
            .then(
              () => false,
              () => true
            )
            .then((signOutFailed) => {
              clearOktaSessionInfo();

              if (signOutFailed) {
                window.location.href = '/login';
              }
            })
        );
    },

    changePassword: ({ newPassword, oldPassword }) =>
      changePasswordMutation.mutateAsync({ newPassword, oldPassword }),

    setNewFilesCount: () => {
      queryClient.invalidateQueries({ queryKey: currentUserKeys.filesCount() });
    },

    setNewInsightsCount: () => {
      queryClient.invalidateQueries({
        queryKey: currentUserKeys.insightsCount(),
      });
    },

    decrementInsightsCount: () => {
      queryClient.setQueryData<number>(currentUserKeys.insightsCount(), (old) =>
        old && old > 0 ? old - 1 : 0
      );
    },
  };
};
